import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { BusinessProfileVersion } from '@/features/tax-profile/business-profiles/BusinessProfile'
import type { BusinessProfileStore } from '@/features/tax-profile/business-profiles/BusinessProfileStore'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'

/**
 * View model for the delete business profile dialog.
 */
export class DeleteBusinessProfileDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  @observable accessor businessProfile: BusinessProfileVersion | null = null

  constructor(
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _businessProfileStore: BusinessProfileStore,
  ) {}

  /**
   * Prompts for and performs deletion if accepted.
   *
   * @param profile
   * @param signal
   * @returns A boolean promise that resolves to true if the
   *  profile was deleted, or false if the operation was cancelled or
   *  the user declined.
   */
  async prompt(profile: BusinessProfileVersion, signal?: AbortSignal) {
    // Close any ongoing prompt
    this.modal.close()

    // Start a new prompt
    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.businessProfile = profile
      this.modal.show()
    })

    // Wait for the user to confirm or cancel
    const response = await responsePromise

    // Close the dialog
    this.modal.close()

    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  delete = task.resolved(async () => {
    if (!this.businessProfile) {
      return
    }

    await this._businessProfileStore
      .delistBusinessProfile(this.businessProfile.id)
      .catch(
        this._createSnackbarErrorHandler(
          'Deleting the business profile failed',
        ),
      )

    this._prompt.respond(true)
    this.modal.close()
  })
}
